import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ObjectId } from "bson";

// --- VOICE ASSISTANT CONFIGURATION ---
const BASE_URL = process.env.REACT_APP_STUDENT_AI_TUTOR_BACKEND_URL;
const WS_URL = `${BASE_URL.replace(/^http/, "ws")}/voice-chat`;

const REQUESTED_UPLINK_SAMPLE_RATE = 16000;
const DEFAULT_TTS_SAMPLE_RATE = 24000;
const PLAYBACK_BUFFER_TARGET_SECONDS = 0.2;

// --- REMOVED: Unused VAD configuration constants ---

const useVoiceAssistant = () => {
  // --- UI State ---
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // --- Voice Assistant State ---
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [error, setError] = useState(null);

  // --- Voice Assistant Refs ---
  const socketRef = useRef(null);
  const inputAudioContextRef = useRef(null);
  const playbackAudioContextRef = useRef(null);
  const streamRef = useRef(null);
  const inputAudioWorkletNodeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentAudioSourceRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const nextStartTimeRef = useRef(0);
  const isAIRespondingRef = useRef(false);

  const serverFormatRef = useRef({
    codec: "pcm_s16le",
    sampleRate: DEFAULT_TTS_SAMPLE_RATE,
    channels: 1,
  });

  // --- Reusable audio chain nodes ---
  const masterGainRef = useRef(null);
  const compressorRef = useRef(null);
  const filterRef = useRef(null);

  // Debug refs
  const bufferedDurationRef = useRef(0);

  const processingMessages = [
    "Getting your study buddy ready...",
    "Loading fun facts",
    "Setting up your learning adventure...",
    "Your AI Tutor is all set to help you ace it!",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingMessageIndex((prevIndex) => {
        if (prevIndex === processingMessages.length - 1) {
          clearInterval(interval);
          setTimeout(() => setIsLoading(false), 500);
          return prevIndex;
        }
        return prevIndex + 1;
      });
    }, 700);
    return () => clearInterval(interval);
  }, [processingMessages.length]);

  // --- CORE AUDIO LOGIC ---

  const ensurePlaybackAudioContext = useCallback(() => {
    // This function remains the same
    if (
      !playbackAudioContextRef.current ||
      playbackAudioContextRef.current.state === "closed"
    ) {
      try {
        playbackAudioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: serverFormatRef.current.sampleRate,
          latencyHint: "interactive",
        });

        masterGainRef.current = playbackAudioContextRef.current.createGain();
        compressorRef.current =
          playbackAudioContextRef.current.createDynamicsCompressor();
        filterRef.current =
          playbackAudioContextRef.current.createBiquadFilter();

        masterGainRef.current.gain.value = 0.9;
        filterRef.current.type = "highpass";
        filterRef.current.frequency.value = 120;
        compressorRef.current.threshold.value = -24;
        compressorRef.current.knee.value = 30;
        compressorRef.current.ratio.value = 3;

        masterGainRef.current
          .connect(filterRef.current)
          .connect(compressorRef.current)
          .connect(playbackAudioContextRef.current.destination);

        nextStartTimeRef.current =
          playbackAudioContextRef.current.currentTime + 0.05;
      } catch (e) {
        console.error("❌ Failed to create AudioContext:", e);
        setError("Web Audio API is not supported by this browser.");
      }
    }

    if (
      playbackAudioContextRef.current &&
      playbackAudioContextRef.current.state === "suspended"
    ) {
      playbackAudioContextRef.current.resume();
    }
    return playbackAudioContextRef.current;
  }, []);

  const stopAndClearPlayback = useCallback((reason = "unknown") => {
    // This function remains the same
    console.warn(`Stopping playback and flushing queue (reason: ${reason}).`);
    try {
      if (currentAudioSourceRef.current) {
        currentAudioSourceRef.current.stop(0);
        currentAudioSourceRef.current.disconnect();
        currentAudioSourceRef.current = null;
      }
      if (
        playbackAudioContextRef.current &&
        playbackAudioContextRef.current.state !== "closed"
      ) {
        playbackAudioContextRef.current.close();
      }
    } catch (e) {
      console.log("Audio cleanup completed.");
    }
    playbackAudioContextRef.current = null;
    masterGainRef.current = null;
    compressorRef.current = null;
    filterRef.current = null;
    playbackQueueRef.current = [];
    bufferedDurationRef.current = 0;
    isPlayingRef.current = false;
    isAIRespondingRef.current = false;
    nextStartTimeRef.current = 0;
  }, []);

  // --- REMOVED: Unused VAD functions (calculateZCR, calculateSpectralRolloff, detectVoiceActivity) ---

  const int16ToFloat32 = useCallback((int16) => {
    // This function remains the same
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      const s = int16[i];
      float32[i] = s < 0 ? s / 32768.0 : s / 32767.0;
    }
    return float32;
  }, []);

  const processAndPlayQueue = useCallback(() => {
    // This function remains the same
    const audioCtx = ensurePlaybackAudioContext();
    if (!audioCtx || playbackQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    const totalSamples = playbackQueueRef.current.reduce(
      (sum, c) => sum + c.samples.length,
      0
    );
    const consolidatedSamples = new Float32Array(totalSamples);
    let offset = 0;
    for (const chunk of playbackQueueRef.current) {
      consolidatedSamples.set(chunk.samples, offset);
      offset += chunk.samples.length;
    }

    const sampleRate = playbackQueueRef.current[0].sampleRate;
    playbackQueueRef.current = [];
    bufferedDurationRef.current = 0;

    try {
      const buffer = audioCtx.createBuffer(
        1,
        consolidatedSamples.length,
        sampleRate
      );
      buffer.copyToChannel(consolidatedSamples, 0, 0);

      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(masterGainRef.current);
      currentAudioSourceRef.current = source;

      const now = audioCtx.currentTime;
      const startTime = Math.max(now, nextStartTimeRef.current);

      source.start(startTime);
      nextStartTimeRef.current = startTime + buffer.duration;

      if (!isAIRespondingRef.current) {
        isAIRespondingRef.current = true;
      }

      source.onended = () => {
        if (currentAudioSourceRef.current === source) {
          currentAudioSourceRef.current = null;
        }
        isPlayingRef.current = false;

        if (playbackQueueRef.current.length > 0) {
          processAndPlayQueue();
        } else {
          isAIRespondingRef.current = false;
        }
      };
    } catch (err) {
      console.error("❌ Error scheduling audio buffer:", err);
      isPlayingRef.current = false;
      isAIRespondingRef.current = false;
    }
  }, [ensurePlaybackAudioContext]);

  const handleIncomingAudio = useCallback(
    (arrayBuffer) => {
      // This function remains the same
      if (arrayBuffer.byteLength % 2 !== 0) {
        arrayBuffer = arrayBuffer.slice(0, arrayBuffer.byteLength - 1);
      }
      const int16 = new Int16Array(arrayBuffer);
      const float32 = int16ToFloat32(int16);
      const sampleRate = serverFormatRef.current.sampleRate;
      const chunkDurationMs = (float32.length / sampleRate) * 1000;
      playbackQueueRef.current.push({ samples: float32, sampleRate });
      bufferedDurationRef.current += chunkDurationMs;
      const targetBufferMs = PLAYBACK_BUFFER_TARGET_SECONDS * 1000;
      if (
        bufferedDurationRef.current >= targetBufferMs &&
        !isPlayingRef.current
      ) {
        isPlayingRef.current = true;
        processAndPlayQueue();
      }
    },
    [int16ToFloat32, processAndPlayQueue]
  );

  const connectWS = useCallback(
    (convId) => {
      // This function remains the same
      return new Promise((resolve, reject) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          resolve();
          return;
        }
        setConnectionStatus("connecting");
        const ws = new WebSocket(WS_URL);
        socketRef.current = ws;
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          setConnectionStatus("connected");
          ws.send(
            JSON.stringify({
              type: "start",
              config: { language_code: "en-US", conversation_id: convId },
            })
          );
          resolve();
        };

        ws.onmessage = (event) => {
          if (typeof event.data === "string") {
            try {
              const parsed = JSON.parse(event.data);
              if (parsed?.type === "clear") {
                stopAndClearPlayback("server_clear_command");
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: "clear_ack" }));
                }
                return;
              }
              if (parsed?.type === "format") {
                const newSampleRate =
                  parsed.sampleRate || DEFAULT_TTS_SAMPLE_RATE;
                serverFormatRef.current = {
                  ...serverFormatRef.current,
                  ...parsed,
                  sampleRate: newSampleRate,
                };
              }
            } catch (e) {
              console.warn("Failed to parse server JSON", e);
            }
          } else if (event.data instanceof ArrayBuffer) {
            if (
              playbackAudioContextRef.current ||
              ensurePlaybackAudioContext()
            ) {
              handleIncomingAudio(event.data);
            }
          }
        };

        ws.onerror = (err) => {
          setConnectionStatus("error");
          setError("WebSocket connection failed.");
          reject(err);
        };

        ws.onclose = () => {
          setConnectionStatus("disconnected");
          setIsRecording(false);
          stopAndClearPlayback("websocket_close");
        };
      });
    },
    [handleIncomingAudio, stopAndClearPlayback, ensurePlaybackAudioContext]
  );

  const startRecording = useCallback(async () => {
    if (isRecording) return;
    setError(null);
    setIsRecording(true);

    try {
      const newConversationId = new ObjectId().toString();
      // setConversationId(newConversationId); // State is not needed, ID is passed directly
      await connectWS(newConversationId);

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
      });
      inputAudioContextRef.current = audioCtx;

      // --- MODIFIED: Simplified worklet code to remove VAD ---
      const inputWorkletCode = `
        class AudioProcessor extends AudioWorkletProcessor {
          constructor() {
            super();
            this.bufferSize = 2048;
            this._buffer = new Float32Array(0);
          }
          process(inputs) {
            const channel = inputs[0]?.[0];
            if (!channel) return true;

            const newBuf = new Float32Array(this._buffer.length + channel.length);
            newBuf.set(this._buffer, 0);
            newBuf.set(channel, this._buffer.length);
            this._buffer = newBuf;

            while (this._buffer.length >= this.bufferSize) {
              const chunk = this._buffer.subarray(0, this.bufferSize);
              const int16 = new Int16Array(chunk.length);
              for (let i = 0; i < chunk.length; i++) {
                let s = Math.max(-1, Math.min(1, chunk[i]));
                int16[i] = s < 0 ? s * 32768 : s * 32767;
              }
              this.port.postMessage(int16.buffer, [int16.buffer]);
              this._buffer = this._buffer.subarray(this.bufferSize);
            }
            return true;
          }
        }
        registerProcessor('audio-processor', AudioProcessor);
      `;

      await audioCtx.audioWorklet.addModule(
        URL.createObjectURL(
          new Blob([inputWorkletCode], { type: "application/javascript" })
        )
      );

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: REQUESTED_UPLINK_SAMPLE_RATE,
        },
      });
      streamRef.current = stream;

      const source = audioCtx.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioCtx, "audio-processor");

      // --- MODIFIED: Simplified message handler ---
      workletNode.port.onmessage = (event) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(event.data);
        }
      };

      source.connect(workletNode);
      inputAudioWorkletNodeRef.current = workletNode;

      if (audioCtx.state === "suspended") await audioCtx.resume();
    } catch (err) {
      setError(
        err.message || "Could not start recording. Check mic permissions."
      );
      setIsRecording(false);
    }
  }, [isRecording, connectWS]); // --- REMOVED: detectVoiceActivity from dependencies

  const stopRecording = useCallback(() => {
    if (!isRecording) return;
    setIsRecording(false);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "stop" }));
      socketRef.current.close();
      socketRef.current = null;
    }

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    inputAudioWorkletNodeRef.current?.disconnect();
    inputAudioWorkletNodeRef.current = null;

    if (
      inputAudioContextRef.current &&
      inputAudioContextRef.current.state !== "closed"
    ) {
      inputAudioContextRef.current.close().then(() => {
        inputAudioContextRef.current = null;
      });
    }
    stopAndClearPlayback("stop_recording");
  }, [isRecording, stopAndClearPlayback]);

  useEffect(() => {
    return () => {
      if (isRecording) {
        stopRecording();
      }
    };
  }, [isRecording, stopRecording]);

  const viewVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const renderLoadingView = () => (
    <motion.div
      key="loading-view"
      variants={viewVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="flex flex-col items-center"
    >
      {/* Simple Loader Circle */}
      <div className="mb-10">
        <motion.div
          className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Status Message */}
      <div className="h-6 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={loadingMessageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-base text-gray-300 font-medium"
          >
            {processingMessages[loadingMessageIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
    </motion.div>
  );

  const renderInteractiveView = () => {
    return (
      <motion.div
        key="interactive-view"
        variants={viewVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="flex flex-col items-center justify-center h-full w-full"
      >
        <button
          className={`relative overflow-hidden transform transition-all duration-300 ease-out px-16 py-7 rounded-full flex items-center gap-4 mx-auto text-lg font-semibold shadow-xl backdrop-blur-xl border-none group
          ${
            isRecording
              ? "bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 text-white shadow-blue-200/40"
              : "bg-cyan-200 text-black hover:bg-gradient-to-br hover:from-yellow-50/95 hover:via-yellow-100/95 hover:to-yellow-200/95 hover:shadow-white/20"
          }`}
          onClick={() => {
            if (isRecording) {
              stopRecording();
            } else {
              startRecording();
            }
          }}
          disabled={connectionStatus === "connecting"}
        >
          {/* Animated Icon */}
          {isRecording ? (
            <motion.div
              className="flex gap-1"
              initial={{ opacity: 0.8 }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <span className="w-1.5 h-6 bg-white rounded-full"></span>
              <span className="w-1.5 h-8 bg-white rounded-full"></span>
              <span className="w-1.5 h-4 bg-white rounded-full"></span>
              <span className="w-1.5 h-7 bg-white rounded-full"></span>
            </motion.div>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-all duration-300 group-hover:text-slate-800 group-hover:scale-110"
            >
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="22" />
            </svg>
          )}

          {/* Label */}
          {isRecording ? "Listening..." : "Talk to Assistant"}
        </button>

        {/* Status Text */}
        <p className="mt-6 text-base text-gray-300">
          {error ? (
            <span className="text-red-400">{error}</span>
          ) : isRecording ? (
            connectionStatus === "connected" ? (
              ""
            ) : (
              "Connecting..."
            )
          ) : (
            ""
          )}
        </p>
      </motion.div>
    );
  };

  return {
    renderLoadingView,
    renderInteractiveView,
    isLoading,
  };
};

export default useVoiceAssistant;
